/**
 * «Вы в кабинете клиента: <имя>» — индикатор контекста в шапке.
 *
 * Пришёл на смену оранжевой полосе с обратным отсчётом. Полоса решала верную
 * задачу (человек должен видеть, что пишет в чужие данные) неверными
 * средствами: тревожный цвет во всю ширину и таймер «доступ ещё N мин»
 * заставляли торопиться и перечитывать одно и то же на каждом экране. Срока у
 * окна больше нет (модель D), торопиться некуда — остаётся ровно одно
 * сообщение: в чьих данных ты стоишь и как выйти.
 *
 * AK-664 — вторая итерация после UAT. Сдержанность оказалась чрезмерной:
 * выход был спрятан в выпадашку, и founder на staging описал состояние как
 * «провалился в чужой кабинет». Поэтому теперь:
 *   * контекст читается фразой целиком («Вы в кабинете клиента: …»), а не
 *     подписью «Клиент:», которую можно принять за фильтр;
 *   * выход — ВИДИМАЯ кнопка с именем своего кабинета, а не пункт меню,
 *     который сначала надо найти;
 *   * рамка/фон заметнее прежних, но тон прежний — accent-brand, не
 *     destructive: это рабочий режим, а не авария.
 */
import { useState } from 'react';
import { DoorOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { describeApiError } from '@/lib/apiError';
import { exitClientWindow } from '@/lib/consultant/api';
import { invalidateConsultantProfile } from '@/lib/consultant/useConsultantProfile';
import type { ClientWindow } from '@/lib/consultant/types';

export interface ClientContextIndicatorProps {
  window: ClientWindow;
  /** Куда уходим после выхода. По умолчанию — перезагрузка текущей страницы. */
  onExited?: () => void;
  /** Название своего кабинета — подпись кнопки возврата. */
  ownTenantName?: string | null;
  compact?: boolean;
}

export function ClientContextIndicator({
  window: win,
  onExited,
  ownTenantName = null,
  compact = false,
}: ClientContextIndicatorProps) {
  const [leaving, setLeaving] = useState(false);
  const name = win.tenant_name ?? 'без названия';
  const own = ownTenantName?.trim();
  const backLabel = own ? `Вернуться в «${own}»` : 'Вернуться к себе';

  const handleExit = async () => {
    setLeaving(true);
    try {
      await exitClientWindow();
      invalidateConsultantProfile();
      if (onExited) onExited();
      // Полная перезагрузка, а не мягкая навигация: сменился токен (в нём
      // больше нет viewing_tenant_id), и весь загруженный стейт относится к
      // данным клиента. Оставить его на экране — то же враньё, от которого
      // индикатор и защищает.
      else globalThis.location.reload();
    } catch (err) {
      // Молчаливый отказ здесь хуже любого текста: человек остаётся в чужих
      // данных, уверенный, что вышел.
      toast.error(describeApiError(err));
      setLeaving(false);
    }
  };

  return (
    <div
      data-testid="client-context-indicator"
      className={cn(
        'flex min-w-0 items-center gap-2 rounded-md border border-accent-brand',
        'bg-accent-brand/15 px-2 py-1 text-sm text-foreground',
      )}
      title={`Вы работаете в данных клиента ${name}. Всё, что вы делаете, сохраняется ему.`}
    >
      <span className="shrink-0 text-xs text-muted-foreground">
        {compact ? 'Клиент:' : 'Вы в кабинете клиента:'}
      </span>
      <span className={cn('truncate font-semibold', compact ? 'max-w-[100px]' : 'max-w-[180px]')}>
        {name}
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-7 shrink-0 px-2"
        disabled={leaving}
        onClick={() => void handleExit()}
        data-testid="client-window-exit"
        title={backLabel}
        /* На мобиле кнопка схлопывается в один глиф, а `title` на тач-экране
           не показывается никогда — без aria-label остаётся безымянная
           иконка «дверь», и выход снова приходится угадывать. */
        aria-label={backLabel}
      >
        <DoorOpen className="h-3.5 w-3.5" />
        {!compact && <span className="ml-1 max-w-[160px] truncate">{backLabel}</span>}
      </Button>
    </div>
  );
}
